import { useEffect } from 'react';

import { LoginState } from '@treneo/models';
import type { ILanguage } from '@treneo/shared/constants';
import { appDefaultLanguageCode, supportedLanguages } from '@treneo/shared/constants';
import { useLoginStateFromContext } from '@treneo/shared/contexts';
import { useAppTranslations } from '@treneo/shared/i18n';

import { useCurrentUserQuery, useSaveLanguage } from '../data';

export const useCurrentLanguage = (): ILanguage => {
  const { mutateAsync: saveLanguageMutation } = useSaveLanguage();
  const { i18n } = useAppTranslations();
  const loginStateFromContext = useLoginStateFromContext();
  const loginState = loginStateFromContext ?? LoginState.NOT_LOGGED;
  const { data: userData } = useCurrentUserQuery({ enabled: true });
  const userLanguage = userData?.languageCode;

  const currentLanguage = () => {
    const i18nLanguageMatch = supportedLanguages.find(
      supportedLanguage => supportedLanguage.code === i18n.language
    );

    if (i18nLanguageMatch) {
      return i18nLanguageMatch;
    }

    if (userLanguage) {
      const foundLanguage = supportedLanguages.find(
        supportedLanguage => supportedLanguage.code === userLanguage
      );
      if (foundLanguage) {
        return foundLanguage;
      }
    }

    const defaultLanguage = supportedLanguages.find(
      supportedLanguage => supportedLanguage.code === appDefaultLanguageCode
    );

    return defaultLanguage ?? supportedLanguages[0];
  };

  useEffect(() => {
    const isCurrentLanguageSupported = supportedLanguages.some(
      supportedLanguage => supportedLanguage.code === i18n.language
    );

    if (!isCurrentLanguageSupported && i18n.language !== appDefaultLanguageCode) {
      const isLogged = loginState === LoginState.LOGGED;
      void saveLanguageMutation({ languageCode: appDefaultLanguageCode, isLogged });
    }
  }, [i18n, loginState, userLanguage, saveLanguageMutation]);

  return currentLanguage();
};
